import React, { useState, useEffect } from 'react';
import { Link, useRouteMatch, useParams, useHistory } from 'react-router-dom';
import LabelledTextBox from '../../components/LabelledTextBox';
import { dateOptions } from '../../utils/dates';
import LabelledPicker from '../../components/LabelledPicker';
import { getFlowDevice, updateFlowDevice } from '../../services/api';
import { makeFlowDevice } from '../../models';
import EventEmitter from '../../services/events';

const EditFlowDevice = () => {
  const [flowDevice, setFlowDevice] = useState(null);
  const [saving, setSaving] = useState(false);
  const { path, url } = useRouteMatch();
  const { flowDeviceId } = useParams();
  const history = useHistory();

  useEffect(() => {
    setupFlowDevice();
  }, [flowDeviceId, history.location]);

  const setupFlowDevice = async () => {
    const result = await getFlowDevice(flowDeviceId);
    setFlowDevice(makeFlowDevice(result));
  };

  const save = async () => {
    try {
      setSaving(true);
      flowDevice.validate();
      const result = await updateFlowDevice(flowDevice);
      history.replace('/flowDevices');
    } catch (error) {
      console.log(error);
      if (error.name === 'ValidationError') {
        EventEmitter.emit('show-error', { title: 'Error Saving Flow Device', body: error.message.split('\n').map((str, idx) => <p key={idx}>{str}</p>) });
      } else {
        EventEmitter.emit('show-error', { title: 'Error Saving Flow Device', body: 'Unable to save flow device.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    history.replace('/flowDevices');
  };

  const onChange = e => {
    let value = e.target.value;
    let name = e.target.name;
    if (name === 'defaultDevice') {
      value = value === 'true';
    }
    setFlowDevice(makeFlowDevice({ ...flowDevice, [name]: value }));
  };

  const onChangeKFactor = (e, idx) => {
    let value = e.target.value;
    let name = e.target.name;
    const kFactors = flowDevice.flowDeviceKFactors.map((kf, i) => {
      if (i === idx) {
        return { ...kf, [name]: value };
      }
      return kf;
    });
    setFlowDevice(makeFlowDevice({ ...flowDevice, flowDeviceKFactors: kFactors }));
  };

  const onBlur = e => {
    const type = e.target.type;
    let value = e.target.value;
    let name = e.target.name;
    
    if (name === 'defaultDevice') {
      value = value === 'true';
    }

    switch (type) {
      case "number":
        value = +value;
        break;
    }

    setFlowDevice(makeFlowDevice({ ...flowDevice, [name]: value }));
  };
  
  const addKFactor = () => {
    const kFactors = [...flowDevice.flowDeviceKFactors, { kFactor: 0, coefficient: 0, minPsi: 0, maxPsi: 0 }];
    setFlowDevice(makeFlowDevice({ ...flowDevice, flowDeviceKFactors: kFactors }));
  };
  
  const removeKFactor = (idx) => {
    const kFactors = flowDevice.flowDeviceKFactors.filter((kf, i) => i !== idx);
    setFlowDevice(makeFlowDevice({ ...flowDevice, flowDeviceKFactors: kFactors }));
  };
  
  return (
    <div>
      <h3>Edit Flow Device</h3>
      {saving && <div className="spinner"></div>}
      {flowDevice ? (
        <>
          <div className="details">
            <LabelledTextBox type="text" label="Name" value={flowDevice.name} name="name" onChange={onChange} onBlur={onBlur}/>
            <LabelledTextBox type="number" label="Diameter" value={flowDevice.diameter} name="diameter" onChange={onChange} onBlur={onBlur}/>
            <LabelledPicker label="Default Device" value={flowDevice.defaultDevice} name="defaultDevice" onChange={onChange} onBlur={onBlur}>
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </LabelledPicker>
          </div>
          <div className="k-factors">
            {flowDevice.flowDeviceKFactors?.map((kFactor, idx) => (
              <div key={idx} className="k-factor card">
                <div className="k-factor-psi">
                  <div className="labelled-text-box">
                    <span className="label">PSI Range</span>
                    <div className="input">
                      <div className="labelled-text-box-partial">
                        <span className="label-partial">Min PSI</span>
                        <input type="number" value={kFactor.minPsi?.toString() ?? ''}
                          name="minPsi"
                          onChange={(e) => onChangeKFactor(e, idx)}
                          onBlur={onBlur}
                          placeholder="Min PSI"
                        />
                      </div>
                      <div className="labelled-text-box-partial">
                        <span className="label-partial">Max PSI</span>
                        <input type="number" value={kFactor.maxPsi?.toString() ?? ''}
                          name="maxPsi"
                          onChange={(e) => onChangeKFactor(e, idx)}
                          onBlur={onBlur}
                          placeholder="Max PSI"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <LabelledTextBox type="text" label="K-Factor" value={kFactor.kFactor} name="kFactor" onChange={(e) => onChangeKFactor(e, idx)} onBlur={onBlur}/>
                <LabelledTextBox type="text" label="Coefficient" value={kFactor.coefficient} name="coefficient" onChange={(e) => onChangeKFactor(e, idx)} onBlur={onBlur}/>
                <div className="form-actions">
                  <div className="link" onClick={() => removeKFactor(idx)}>Remove K-Factor</div>
                </div>
              </div>
            ))}
          </div>
          <div className="form-actions">
            <div className="link" onClick={addKFactor}>Add K-Factor</div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>Save</button>
          </div>
        </>
      ) : (
        <span className="spinner"></span>
      )}
    </div>
  );
};

export default EditFlowDevice;
